/**
 * src/engine/ratePaths.ts — resolve a SOFR value for each month (§6.5).
 *
 * Supports flat / linear / custom-anchors / stress paths. The resolved SOFR is always
 * floored at 0% first, then clamped to the optional user floor/cap.
 */
import type { RatePath } from '@/types';
import { isRateStressPreset, rateStressDeltaBps } from './stressPresets';

/** Raw (pre-floor/cap) SOFR for a month from the path definition. */
function rawSofr(path: RatePath, monthIndex: number): number {
  const m = Math.max(0, monthIndex);
  switch (path.type) {
    case 'flat':
      return path.baseRatePct;
    case 'linear':
      return path.baseRatePct + ((path.annualDeltaBps ?? 0) / 100) * (m / 12);
    case 'custom':
      return interpolateAnchors(path, m);
    case 'stress':
      if (path.stressPreset && isRateStressPreset(path.stressPreset)) {
        return path.baseRatePct + rateStressDeltaBps(path.stressPreset, m) / 100;
      }
      // Asset-class stress presets do not move SOFR; hold the base rate flat.
      return path.baseRatePct;
  }
}

/** Linear interpolation across custom anchors; flat before the first / after the last. */
function interpolateAnchors(path: RatePath, m: number): number {
  const anchors = (path.anchors ?? []).slice().sort((a, b) => a.monthIndex - b.monthIndex);
  if (anchors.length === 0) return path.baseRatePct;
  const first = anchors[0]!;
  if (m <= first.monthIndex) return first.baseRate;
  // Find the bracketing segment (strict lower bound skips zero-width duplicate anchors).
  for (let i = 0; i < anchors.length - 1; i++) {
    const a = anchors[i]!;
    const b = anchors[i + 1]!;
    if (m > a.monthIndex && m <= b.monthIndex) {
      const frac = (m - a.monthIndex) / (b.monthIndex - a.monthIndex);
      return a.baseRate + (b.baseRate - a.baseRate) * frac;
    }
  }
  // m is beyond the last anchor → hold flat.
  return anchors[anchors.length - 1]!.baseRate;
}

/**
 * Resolve the effective SOFR for `monthIndex`: floor at 0%, then clamp to the optional
 * user floor/cap (§6.4 step 2, §6.5).
 */
export function resolveBaseRate(path: RatePath, monthIndex: number): number {
  const floored = Math.max(0, rawSofr(path, monthIndex));
  const lower = path.floorPct ?? 0;
  const upper = path.capPct ?? Infinity;
  if (floored < lower) return lower;
  if (floored > upper) return upper;
  return floored;
}
